/* eslint-disable @cspell/spellchecker */
import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import aiOnlyModeLogo from '../../../assets/images/ai_only_mode.svg';
import dashworksLogo from '../../../assets/images/header_logo.svg';
import webModeLogo from '../../../assets/images/web_mode.svg';
import { UIIcon } from '../../controls/ui/UIIcon/UIIcon';
import { Card, CardContent } from '../../shadcn/lib/components/card';

export const QAUseCases: FC<{
  shouldStream: boolean;
  onReadyToShowNext: (timer: number) => void;
}> = ({ shouldStream, onReadyToShowNext }) => {
  const [currentChild, setCurrentChild] = useState(1);

  useEffect(() => {
    if (!shouldStream) {
      setCurrentChild(99);
      return;
    }

    const maxChildren = 4;
    let timeoutId: NodeJS.Timeout;

    if (currentChild < maxChildren) {
      timeoutId = setTimeout(() => {
        setCurrentChild((prevChild) => prevChild + 1);
      }, 500);
    }

    if (currentChild === maxChildren) {
      onReadyToShowNext(1000);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [shouldStream, currentChild, onReadyToShowNext]);

  return (
    <div className="flex gap-1.5">
      {currentChild >= 1 && (
        <UseCaseCard shouldStream={shouldStream}>
          <strong className="text-sm">Dash AI connects to your apps</strong>
          <div className="flex md:flex-row flex-col gap-1 md:gap-2">
            <div className="flex-1 flex flex-col justify-center gap-1 md:gap-2 lg:gap-3">
              <div className="flex gap-1 md:gap-2 lg:gap-3">
                <UIIcon name="notion" type="apps" />
                <UIIcon name="linear" type="apps" />
              </div>
              <div className="flex gap-1 md:gap-2 lg:gap-3">
                <UIIcon name="asana" type="apps" />
                <UIIcon name="slack" type="apps" />
              </div>
            </div>
            <div className="md:visible invisible flex-1 flex gap-1 items-center">
              <div className="w-2 md:w-4 lg:w-8 h-0.5 bg-gradient-to-r from-moss-10 to-moss-30" />
              <svg
                fill="none"
                height="10"
                viewBox="0 0 10 10"
                width="10"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="5" cy="5" fill="#51CF66" r="4.5" stroke="#86EFAC" />
              </svg>
              <div className="w-2 md:w-4 lg:w-8 h-0.5 bg-gradient-to-l from-moss-10 to-moss-30" />
            </div>
            <div className="flex-1 flex bg-gray-10 p-1 md:p-2 lg:p-3 rounded-lg">
              <img src={dashworksLogo} style={{ width: 38 }} />
            </div>
          </div>
        </UseCaseCard>
      )}
      {currentChild >= 2 && (
        <UseCaseCard shouldStream={shouldStream}>
          <strong className="text-sm">Answers work questions</strong>
          <div className="flex md:flex-row flex-col gap-2 items-center bg-cloud-10 py-3 px-2 rounded-lg border border-solid border-gray-20">
            <div className="flex bg-purple-10 p-2 rounded border border-solid border-purple-30">
              <img src={dashworksLogo} style={{ width: 24 }} />
            </div>
            <div className="text-sm text-cloud-40">Update on Acme deal?</div>
          </div>
        </UseCaseCard>
      )}
      {currentChild >= 3 && (
        <UseCaseCard shouldStream={shouldStream}>
          <strong className="text-sm">Write emails, blogs, updates etc</strong>
          <div className="flex md:flex-row flex-col gap-2 items-center bg-cloud-10 py-3 px-2 rounded-lg border border-solid border-gray-20">
            <img src={aiOnlyModeLogo} style={{ width: 40 }} />
            <div className="text-sm text-cloud-40">Acme AI tweet ideas</div>
          </div>
        </UseCaseCard>
      )}
      {currentChild >= 4 && (
        <UseCaseCard shouldStream={shouldStream}>
          <strong className="text-sm">Searches the web real time</strong>
          <div className="flex md:flex-row flex-col gap-2 items-center bg-cloud-10 py-3 px-2 rounded-lg border border-solid border-gray-20">
            <img src={webModeLogo} style={{ width: 40 }} />
            <div className="text-sm text-cloud-40">When did Acme launch?</div>
          </div>
        </UseCaseCard>
      )}
    </div>
  );
};

const UseCaseCard: FC<{ shouldStream: boolean }> = ({
  children,
  shouldStream,
}) => {
  return (
    <Card
      className={classNames('shadow-md border border-solid border-cloud-15', {
        'animate-in fade-in ease-in-out duration-500': shouldStream,
      })}
    >
      <CardContent className="flex flex-col gap-2 items-center justify-center">
        {children}
      </CardContent>
    </Card>
  );
};
